import { CUSTOM_AGREEMENT_ID } from '../data/customAgreementData';
import { isFollowOnAgreement } from '../data/followOnAgreementData';

export type AgreementTemplateVariant = 'standard' | 'custom' | 'follow-on';

/**
 * Determines if an agreement should use custom data based on agreement ID
 * @param agreementId The agreement ID to check
 * @returns true if the agreement should use custom data, false otherwise
 */
export const shouldUseCustomAgreementData = (agreementId: string | null | undefined): boolean => {
  if (!agreementId) {
    return false;
  }
  
  // Check if the agreement ID matches our custom agreement ID
  return agreementId === CUSTOM_AGREEMENT_ID;
};

export const getAgreementTemplateVariant = (
  agreementId: string | null | undefined
): AgreementTemplateVariant => {
  if (isFollowOnAgreement(agreementId)) {
    return 'follow-on';
  }
  if (shouldUseCustomAgreementData(agreementId)) {
    return 'custom';
  }
  return 'standard';
};

/**
 * Determines if an agreement should use custom data based on either agreement ID or signature ID
 * This function handles both direct agreement access and signature-based access
 * @param agreementId The direct agreement ID (if available)
 * @param signatureId The signature ID (if accessing via signature link)
 * @param agreementFromSignatureContext The agreement data loaded from signature context (if available)
 * @returns true if the agreement should use custom data, false otherwise
 */
export const getAgreementTemplateVariantFromContext = (
  agreementId: string | null | undefined,
  signatureId?: string | null,
  agreementFromSignatureContext?: { id?: string }
): AgreementTemplateVariant => {
  if (agreementId) {
    return getAgreementTemplateVariant(agreementId);
  }

  if (agreementFromSignatureContext?.id) {
    return getAgreementTemplateVariant(agreementFromSignatureContext.id);
  }

  return 'standard';
};

export const shouldUseCustomAgreementDataFromContext = (
  agreementId: string | null | undefined,
  signatureId?: string | null,
  agreementFromSignatureContext?: { id?: string }
): boolean => {
  return getAgreementTemplateVariantFromContext(
    agreementId,
    signatureId,
    agreementFromSignatureContext
  ) === 'custom';
};

export const shouldUseFollowOnAgreementDataFromContext = (
  agreementId: string | null | undefined,
  signatureId?: string | null,
  agreementFromSignatureContext?: { id?: string }
): boolean => {
  return getAgreementTemplateVariantFromContext(
    agreementId,
    signatureId,
    agreementFromSignatureContext
  ) === 'follow-on';
};

/**
 * List of agreement IDs that should use custom data
 * This can be expanded in the future if more custom agreements are needed
 */
export const CUSTOM_AGREEMENT_IDS = [CUSTOM_AGREEMENT_ID];

/**
 * Helper function to check if a signature ID is associated with a custom agreement
 * This would require the agreement data to be loaded first to determine the agreement ID
 * @param signatureId The signature ID
 * @param loadedAgreementData The agreement data that has been loaded
 * @returns true if the signature is for a custom agreement, false otherwise
 */
export const isSignatureForCustomAgreement = (
  signatureId: string | null | undefined,
  loadedAgreementData?: { id?: string }
): boolean => {
  if (!signatureId || !loadedAgreementData?.id) {
    return false;
  }
  
  return shouldUseCustomAgreementData(loadedAgreementData.id);
};